import React from 'react';
import clsx from 'clsx';
import { InputNumber } from '@components/framework/input/InputNumber';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { PriceMatrix, PriceTableForm } from '@components/ringil3/Features/Contracted/types.ts';
import { editCell, getCell, levelPercent, priceRowLabel } from '@utils/contractedPriceTable.ts';

interface ComponentProps {
  table: PriceTableForm;
  prices: PriceMatrix;
  onChange?: (prices: PriceMatrix) => void;
  readOnly?: boolean;
  /** The cells that price the shipment (level on the deadline day × the applicable row of each MU), outlined. */
  highlight?: { levelKey: string; rowKeys: string[] } | null;
  dataTestIdPrefix?: string;
}

const formatPercent = (value: number) => `${value > 0 ? '+' : ''}${value.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %`;

/**
 * "Smluvní ceny" of one carrier: rows (MU + laden factor or Výchozí) × level columns, the price of one MU in the table currency.
 * In Procentuální mode the default level column is the one to type; it fills the rest of its row, every cell stays
 * editable.
 */
const CarrierPriceMatrix: React.FC<ComponentProps> = ({ table, prices, onChange, readOnly = false, highlight, dataTestIdPrefix = 'price-matrix' }) => {
  const { tr } = useTranslationLgs();
  const percentage = table.inputMode === 'PERCENTAGE';

  if (table.rows.length === 0) {
    return (
      <div className={'rounded bg-content-quaternary/30 px-4 py-3 text-sm text-content-secondary'}>
        {tr('PriceTable.noRows', 'Tabulka zatím nemá žádnou manipulační jednotku. Doplňte ji v nastavení tabulky.')}
      </div>
    );
  }
  const defaultLabel = tr('PriceTable.defaultLadenFactor', 'Výchozí');

  return (
    <div className={'overflow-x-auto'}>
      <table className={'text-sm border-collapse'}>
        <thead>
          <tr className={'bg-[#e9ecef] text-left'}>
            <th className={'px-4 py-3 font-medium sticky left-0 bg-[#e9ecef] whitespace-nowrap'}>{tr('PriceTable.rowHeader', 'MJ · LF')}</th>
            {table.levels.map(level => {
              const percent = levelPercent(table, level.key);
              return (
                <th key={level.key} className={'px-1 py-2 font-medium whitespace-nowrap min-w-[5.5rem]'}>
                  <div>{level.name}</div>
                  {percentage && percent !== null && <div className={'text-xs font-normal text-content-secondary'}>{formatPercent(percent)}</div>}
                </th>
              );
            })}
            <th className={'w-full'} />
          </tr>
        </thead>
        <tbody>
          {table.rows.map(row => (
            <tr key={row.key} className={'border-b border-solid border-content-quaternary bg-white'}>
              <td className={'px-4 py-2 sticky left-0 bg-white whitespace-nowrap'}>{priceRowLabel(row, defaultLabel)}</td>
              {table.levels.map(level => {
                const isDefault = percentage && level.key === table.defaultLevelKey;
                const isHighlighted = highlight && highlight.levelKey === level.key && highlight.rowKeys.includes(row.key);
                return (
                  <td key={level.key} className={'px-0.5 py-1'}>
                    <InputNumber
                      value={getCell(prices, level.key, row.key)}
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      min={0}
                      locale={'cs-CZ'}
                      disabled={readOnly}
                      onValueChange={e => onChange?.(editCell(table, prices, level.key, row.key, e.value ?? null))}
                      inputClassName={clsx(
                        'w-[5rem] !px-2',
                        percentage && !isDefault && '!bg-[#f1f3f5]',
                        isDefault && '!border-content-primary',
                        isHighlighted && '!border-2 !border-content-brand',
                      )}
                      data-testid={`${dataTestIdPrefix}-${table.rows.indexOf(row)}-${table.levels.indexOf(level)}`}
                    />
                  </td>
                );
              })}
              <td />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CarrierPriceMatrix;
